/**
 * Configurações que seguem a PESSOA, não o computador (pedido do Leopoldo,
 * 26/10/2026: "salvas para o usuário em todos os computadores e não
 * diferentes por computador").
 *
 * As telas continuam lendo e gravando no localStorage como sempre — nada
 * espera rede. Este módulo espelha as chaves de `sincroniza()` no doc
 * `userPrefs/{uid}` do Firestore (`v`: chave → texto gravado):
 *   - gravou uma delas aqui → sobe pra conta (junta o que mudar em ~0,8 s);
 *   - mudou na conta (outro computador) → desce pro localStorage e a página
 *     recebe um evento `storage`, o mesmo que as telas já escutam pra
 *     acompanhar outra aba.
 * Quem grava não precisa saber disto: o `setItem`/`removeItem` do
 * localStorage passa por aqui (`instalar`). Chave nova de configuração entra
 * na lista abaixo e pronto.
 *
 * Ficam no APARELHO, de propósito (não estão na lista): microfones, motor da
 * escuta e a trava de rede dela, controle de apresentação, unidade do
 * computador e "Todas as unidades", sala da fila, painel de chamada,
 * calibração dos optotipos, tabela de preço da cobrança da recepção, posição
 * solta do chat (pixels da tela), o kill switch do ditado (console), Amplo ↔
 * Estreito da Agenda e da ficha (depende do tamanho da tela: o Amplo do
 * computador abria o cockpit no celular) — e o que não é configuração
 * (diários, recentes, rascunhos, última ficha).
 *
 * Juntar computadores que já tinham preferência: na primeira vez de um
 * computador na conta, o que só ele tem SOBE (nada se perde); o que os dois
 * têm, vale o da conta (quem chegou primeiro). Se as preferências deste
 * computador eram de OUTRA pessoa (máquina compartilhada), nada sobe — só
 * desce o da conta. Escolheu errado: Configurações › Meu Perfil › "Levar as
 * deste computador pra conta" (`levarDesteComputador`).
 */

import { useSyncExternalStore } from 'react';
import { deleteField, doc, onSnapshot, serverTimestamp, setDoc, type Unsubscribe } from 'firebase/firestore';
import { db } from '@/services/firebase/config';

/** Chaves exatas que seguem a conta. */
const CHAVES: ReadonlySet<string> = new Set([
  // Tema gráfico e fonte de digitação (o tour concluído fica no computador:
  // não é configuração, e apagá-lo numa máquina compartilhada reabria o tour)
  'dratento-theme',
  'dratento-typing',
  // Som do chat e onde o chat fica (a posição arrastada, em pixels, fica na tela)
  'dratento-chat-sound',
  'dratento-chat-volume',
  'dratento-chat-position',
  'dratento-chat-vertical',
  'dratento-chat-always-open',
  'dratento-chat-in-dock',
  // Tela de Atendimento e a ficha
  'dratento.cockpit.sanfona',
  'dratento.visual.google',
  'dratento.ficha.evolucao.v1',
  'dratento-agenda-always-open',
  'dratento.clinicalDrawer.open',
  'dratento.patientLastTab',
  'dratento:encounter-mirror-eyes',
  'dratento:encounter-catalog-collapsed',
  'dratento:last-encounter-rail-collapsed',
  'dratento:prontuario-records-col-collapsed',
  // Documentos: observações e padrões da receita de óculos, grade de valores, prévia
  'dratento-lens-obs-templates',
  'dratento-lens-obs-templates-v2',
  'dratento-lens-obs-defaults',
  'dratento-lens-value-grid',
  'dratento.templatePicker.allTypes',
  'textdoc-print-preview',
  'templatesViewMode',
  // Extrair dados: quais modais a IA preenche
  'dratento.extraction.targets.v1',
  // Ditado por voz (o que não depende do aparelho)
  'dratento.voice.wake.modo.v1',
  'dratento.voice.conferir.v1',
  'dratento.voice.conferir.fala.v1',
  'dratento.voice.imprimir.padrao.v1',
  'dratento.voice.jev.v1',
  'dratento.voice.jev.escolha.v1',
  'dratento.voice.manualOpen',
  'dratento.voice.colunaLarga.v1',
  'dratento.voice.layoutColuna.v1',
  // Agenda, Liberar agenda, painel rápido, Balanço
  'dratento.agenda.sidebar.collapsed',
  'dratento.agenda.callSound.muted',
  'dratento.liberar.modo',
  'dratento.quickpanel.freeScroll',
  'dratento.balanco.orientation',
]);

/** Famílias de chave: notas padrão (estrela), gatilhos de frase, canais mudos do chat. */
const PREFIXOS = ['dratento-notes-defaults:', 'dratento-phrase-triggers:', 'dratento-muted-'];

/** Já eram por pessoa no computador (terminam no uid): seguem a conta só as de quem está logado. */
const PREFIXOS_DO_UID = [
  'dratento-session-sections:',
  'dratento-prontuario-rail:',
  'dratento-prontuario-toolbar:',
  'dratento-prontuario-fixed:',
  'dratento-page-fixed:',
  'dratento-view-density:',
  'dratento-rx-picker-mode:',
  'dratento-ficha-header-compact:',
];

/**
 * Versão da lista acima. MEXEU NA LISTA (chave nova), SOBE ESTE NÚMERO: senão
 * os computadores que já juntaram apagam o valor local da chave nova (a conta
 * ainda não tem); com a versão nova, cada um junta de novo — sobe o que só ele
 * tem.
 */
export const VERSAO_LISTA = '5';
/** Este computador já juntou as preferências com a conta desta pessoa (valor = versão da lista). */
const MARCA = (uid: string) => `dratento.prefs.conta.${uid}`;
/** De quem são as preferências deste computador (a última conta que entrou). */
const DONO = 'dratento.prefs.dono';

/** Junta o que mudar em sequência (arrastar um volume, marcar várias estrelas). */
const AGRUPAR_MS = 800;

let uidAtual: string | null = null;
/** A conta foi lida de verdade (servidor): daqui em diante o que muda aqui sobe. */
let pronto = false;
let pararEscuta: Unsubscribe | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
/** Mudou aqui e ainda não subiu (`null` = apagou). A conta não passa por cima delas. */
const pendentes = new Map<string, string | null>();

/** Grava sem passar pela sincronização (o que veio da conta não volta pra ela). */
let gravarLocal: (chave: string, valor: string | null) => void = (chave, valor) => {
  if (valor === null) localStorage.removeItem(chave);
  else localStorage.setItem(chave, valor);
};

export function sincroniza(chave: string, uid: string | null = uidAtual): boolean {
  if (CHAVES.has(chave) || PREFIXOS.some((p) => chave.startsWith(p))) return true;
  return !!uid && chave.endsWith(`:${uid}`) && PREFIXOS_DO_UID.some((p) => chave.startsWith(p));
}

// ─── Situação (pra tela de Configurações) ───

export type SituacaoPrefs = 'fora' | 'conectando' | 'na-conta' | 'sem-acesso';
let situacao: SituacaoPrefs = 'fora';
const ouvintes = new Set<() => void>();
function setSituacao(s: SituacaoPrefs): void {
  if (situacao === s) return;
  situacao = s;
  ouvintes.forEach((f) => f());
}

export function useSituacaoPrefs(): SituacaoPrefs {
  return useSyncExternalStore(
    (f) => {
      ouvintes.add(f);
      return () => ouvintes.delete(f);
    },
    () => situacao,
    () => 'fora' as SituacaoPrefs,
  );
}

// ─── Gravações daqui ───

function ehLocal(s: Storage): boolean {
  try {
    return s === window.localStorage;
  } catch {
    return false;
  }
}

let instalado = false;
function instalar(): void {
  if (instalado || typeof Storage === 'undefined') return;
  instalado = true;
  const proto = Storage.prototype;
  const setItem = proto.setItem;
  const removeItem = proto.removeItem;
  // Só lê o valor de antes das chaves da lista: os diários gravam muito e grande.
  const vigiada = (s: Storage, chave: string) => !!uidAtual && ehLocal(s) && sincroniza(String(chave));
  proto.setItem = function (this: Storage, chave: string, valor: string) {
    if (!vigiada(this, chave)) return setItem.call(this, chave, valor);
    const antes = this.getItem(chave);
    setItem.call(this, chave, valor);
    mudou(String(chave), antes, String(valor));
  };
  proto.removeItem = function (this: Storage, chave: string) {
    if (!vigiada(this, chave)) return removeItem.call(this, chave);
    const antes = this.getItem(chave);
    removeItem.call(this, chave);
    mudou(String(chave), antes, null);
  };
  gravarLocal = (chave, valor) => {
    if (valor === null) removeItem.call(localStorage, chave);
    else setItem.call(localStorage, chave, valor);
  };
  // Fechou a aba logo depois de mexer: sobe já, sem esperar o agrupamento.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void enviar();
  });
}

function mudou(chave: string, antes: string | null, depois: string | null): void {
  if (antes === depois) return;
  // Antes de a conta responder, guarda: sobe junto com a primeira leitura.
  pendentes.set(chave, depois);
  if (pronto) agendar();
}

function agendar(): void {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => void enviar(), AGRUPAR_MS);
}

function enviar(): Promise<void> {
  if (timer) clearTimeout(timer);
  timer = null;
  if (!uidAtual || !pronto || pendentes.size === 0) return Promise.resolve();
  const v: Record<string, unknown> = {};
  for (const [k, valor] of pendentes) v[k] = valor ?? deleteField();
  pendentes.clear();
  // merge: só as chaves que mudaram — outro computador mexendo em outra não se perde.
  return setDoc(doc(db, 'userPrefs', uidAtual), { v, em: serverTimestamp() }, { merge: true }).catch((err) => {
    console.warn('[prefs] não subiu pra conta:', err);
  });
}

// ─── O que vem da conta ───

function chavesLocais(): string[] {
  const out: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k !== null) out.push(k);
    }
  } catch {
    /* storage bloqueado */
  }
  return out;
}

/** Põe a conta no localStorage. `podar`: o que a conta não tem sai daqui também. */
function aplicar(remoto: Record<string, unknown>, podar: boolean): void {
  const mudadas: Array<{ key: string; oldValue: string | null; newValue: string | null }> = [];
  for (const k of new Set([...chavesLocais(), ...Object.keys(remoto)])) {
    if (!sincroniza(k) || pendentes.has(k)) continue;
    const r = remoto[k];
    const novo = typeof r === 'string' ? r : null;
    if (novo === null && !podar) continue;
    const antes = localStorage.getItem(k);
    if (antes === novo) continue;
    try {
      gravarLocal(k, novo);
    } catch {
      continue; // cota cheia: fica a daqui
    }
    mudadas.push({ key: k, oldValue: antes, newValue: novo });
  }
  // Depois de gravar tudo: quem escuta uma chave já acha as outras no lugar.
  for (const m of mudadas) {
    window.dispatchEvent(new StorageEvent('storage', { ...m, storageArea: localStorage, url: window.location.href }));
  }
}

function primeiraLeitura(uid: string, remoto: Record<string, unknown>): void {
  const jaJuntou = localStorage.getItem(MARCA(uid)) === VERSAO_LISTA;
  const dono = localStorage.getItem(DONO);
  const minhas = !dono || dono === uid;
  if (minhas && !jaJuntou) {
    // Primeira vez deste computador nesta conta: o que só ele tem sobe.
    for (const k of chavesLocais()) {
      if (sincroniza(k, uid) && !(k in remoto) && !pendentes.has(k)) pendentes.set(k, localStorage.getItem(k));
    }
  }
  aplicar(remoto, jaJuntou || !minhas);
  gravarLocal(MARCA(uid), VERSAO_LISTA);
  gravarLocal(DONO, uid);
}

/**
 * Liga a sincronização pra quem entrou (AuthContext, só equipe). Resolve
 * depois da primeira leitura da conta — as telas montam já com as
 * preferências dela — ou em `esperaMs`, pra rede lenta não segurar o login
 * (aí as preferências chegam depois, pelo evento `storage`).
 */
export function iniciarPrefsNaConta(uid: string, esperaMs = 2500): Promise<void> {
  if (uidAtual === uid && pararEscuta) return Promise.resolve();
  void pararPrefsNaConta();
  instalar();
  uidAtual = uid;
  pronto = false;
  setSituacao('conectando');
  return new Promise((resolve) => {
    const desiste = setTimeout(resolve, esperaMs);
    pararEscuta = onSnapshot(
      doc(db, 'userPrefs', uid),
      // Com a conta já no cache (entrou de novo na mesma aba), a primeira
      // leitura vem do cache e a do servidor, igual, só muda o `fromCache`:
      // sem isto ela não chega e a primeira leitura nunca acontece.
      { includeMetadataChanges: true },
      (snap) => {
        // Do cache (sem rede): não decide nada sem a conta de verdade — um
        // "não existe" daqui apagaria as preferências deste computador.
        if (snap.metadata.fromCache || uidAtual !== uid) return;
        const remoto = (snap.data()?.v ?? {}) as Record<string, unknown>;
        try {
          if (pronto) return aplicar(remoto, true);
          primeiraLeitura(uid, remoto);
        } catch (err) {
          console.warn('[prefs] não deu pra aplicar as preferências da conta:', err);
          if (pronto) return;
        }
        pronto = true;
        setSituacao('na-conta');
        void enviar();
        clearTimeout(desiste);
        resolve();
      },
      (err) => {
        console.warn('[prefs] sem acesso às preferências da conta (ficam neste computador):', err);
        if (uidAtual === uid) setSituacao('sem-acesso');
        clearTimeout(desiste);
        resolve();
      },
    );
  });
}

/** Saiu (ou trocou de conta): sobe o que faltava e para de ouvir. */
export function pararPrefsNaConta(): Promise<void> {
  pararEscuta?.();
  pararEscuta = null;
  const envio = enviar();
  pendentes.clear();
  pronto = false;
  uidAtual = null;
  setSituacao('fora');
  return Promise.race([envio, new Promise<void>((r) => setTimeout(r, 1500))]);
}

/**
 * A conta fica igual a ESTE computador (o que ele não tem sai da conta) — pra
 * quando a junção da primeira vez escolheu o computador errado. Os outros
 * computadores abertos recebem na hora.
 */
export async function levarDesteComputador(): Promise<number> {
  if (!uidAtual || !pronto) throw new Error('A conta ainda não respondeu — tente de novo em instantes.');
  const uid = uidAtual;
  const v: Record<string, string> = {};
  for (const k of chavesLocais()) {
    const valor = localStorage.getItem(k);
    if (valor !== null && sincroniza(k, uid)) v[k] = valor;
  }
  if (timer) clearTimeout(timer);
  timer = null;
  pendentes.clear();
  await setDoc(doc(db, 'userPrefs', uid), { v, em: serverTimestamp() });
  return Object.keys(v).length;
}
